/**
 * Session 88 pt10 — Dashboard alert banner for un-enrolled sales leads.
 *
 * Polls `/api/admin/marketing/unenrolled-leads/count` on mount. If the
 * count is > 0 and the viewer is a platform admin, surfaces a yellow
 * banner with the count and a "View Leads" action that opens the
 * full `/admin/marketing/enrol-leads` page where the operator can
 * checkbox-pick individual leads and bulk-enrol them. Replaces the
 * earlier modal-based "Yes — enrol them" one-shot flow (Session 90 —
 * user feedback: per-lead control is preferred over enrol-all).
 *
 * Counts of 0 hide the banner entirely (no noise).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UserPlus, X } from "@phosphor-icons/react";


export function UnenrolledLeadsBanner() {
  const [count, setCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/marketing/unenrolled-leads/count")
      .then(r => setCount(r.data?.count ?? 0))
      .catch(() => setCount(0));
  }, []);

  if (count <= 0 || dismissed) return null;

  return (
    <div
      data-testid="unenrolled-leads-banner"
      className="flex items-center justify-between gap-4 px-5 py-3 bg-amber-50 border border-amber-200 rounded text-sm"
    >
      <div className="flex items-center gap-3 flex-1">
        <UserPlus
          size={20}
          weight="bold"
          className="text-amber-800 flex-shrink-0"
        />
        <div>
          <div className="font-semibold text-amber-900">
            {count.toLocaleString()} new sales lead
            {count === 1 ? "" : "s"} {count === 1 ? "is" : "are"} ready for
            campaign enrolment.
          </div>
          <div className="text-xs text-amber-800/80 mt-0.5">
            Genuinely enrollable leads only — suppressed contacts, deliberate
            reputation/quality holds and leads already in a workflow are
            excluded and are not offered here.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          data-testid="banner-view-leads"
          onClick={() => navigate("/admin/marketing/enrol-leads")}
          className="bg-amber-900 hover:bg-amber-800 text-amber-50"
          size="sm"
        >
          View Leads
        </Button>
        <button
          data-testid="banner-dismiss"
          onClick={() => setDismissed(true)}
          className="p-1 text-amber-700 hover:text-amber-900"
          title="Dismiss for this session"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
